// src/utils/securityMiddleware.ts
import { toast } from 'react-hot-toast';
import { Sanitizer, RateLimiter } from './sanitizer';

// Enhanced input sanitization using Sanitizer class
export const sanitizeInput = (input: string): string => {
  return Sanitizer.sanitizeHtml(input);
};

// Enhanced SQL injection prevention
export const validateSqlInput = (input: string): boolean => {
  const sanitized = Sanitizer.sanitizeQuery(input);
  return sanitized === input.replace(/['"\\;]/g, '').trim();
};

// Enhanced XSS prevention
export const escapeHtml = (text: string): string => {
  return Sanitizer.sanitizeHtml(text);
};

// CSRF token management
export const generateCSRFToken = (): string => {
  return crypto.randomUUID();
};

export const setCSRFToken = (): string => {
  const token = generateCSRFToken();
  sessionStorage.setItem('csrf-token', token);
  return token;
};

export const getCSRFToken = (): string | null => {
  return sessionStorage.getItem('csrf-token');
};

export const validateCSRFToken = (token: string): boolean => {
  const storedToken = getCSRFToken();
  return storedToken === token;
};

// Enhanced request validation with comprehensive security checks
export const validateRequest = (data: any, userId?: string): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  const richTextFields = new Set([
    'message_content',
    'vision_statement',
    'sustainability_commitment'
  ]);
  const mediaFields = new Set([
    'ceo_photo',
    'company_logo',
    'cover_image',
    'profile_photo',
    'avatar'
  ]);

  const isMediaField = (key: string): boolean => {
    const normalizedKey = key.split('.').pop() || key;
    return mediaFields.has(normalizedKey) || /(_photo|_logo|_image|_url|_link)$/i.test(normalizedKey);
  };
  
  // Rate limiting check
  if (userId && !RateLimiter.isAllowed(userId, 100, 60000)) {
    errors.push('Rate limit exceeded');
    return { isValid: false, errors };
  }
  
  // Deep sanitization and validation
  const sanitizedData = Sanitizer.sanitizeObject(data, {
    richTextKeys: Array.from(richTextFields)
  });
  
  // Check for common attack patterns
  const checkValue = (value: any, key: string) => {
    if (typeof value === 'string') {
      if (richTextFields.has(key.split('.').pop() || key)) {
        if (value.length > 10000) {
          errors.push(`Input too long in ${key}`);
        }

        const richTextValue = Sanitizer.sanitizeRichText(value);
        if (!richTextValue && value.trim()) {
          errors.push(`Potentially malicious input detected in ${key}`);
        }

        return;
      }

      if (isMediaField(key)) {
        if (value.length > 2048) {
          errors.push(`Input too long in ${key}`);
        }

        if (/\sjavascript\s*:|data\s*:\s*text\/html|<\s*script/i.test(value)) {
          errors.push(`Potentially malicious input detected in ${key}`);
        }

        return;
      }

      if (!validateSqlInput(value)) {
        errors.push(`Potentially malicious input detected in ${key}`);
      }
      
      if (value.length > 10000) {
        errors.push(`Input too long in ${key}`);
      }
      
      // Check for suspicious patterns
      const suspiciousPatterns = [
        /eval\s*\(/gi,
        /function\s*\(/gi,
        /\${.*}/gi,
        /<iframe/gi,
        /<object/gi,
        /<embed/gi
      ];
      
      if (suspiciousPatterns.some(pattern => pattern.test(value))) {
        errors.push(`Suspicious pattern detected in ${key}`);
      }
    }
  };
  
  const traverse = (obj: any, path = '') => {
    if (obj === null || obj === undefined) return;
    
    for (const [key, value] of Object.entries(obj)) {
      const currentPath = path ? `${path}.${key}` : key;
      
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        traverse(value, currentPath);
      } else if (Array.isArray(value)) {
        value.forEach((item, index) => {
          if (typeof item === 'object') {
            traverse(item, `${currentPath}[${index}]`);
          } else {
            checkValue(item, `${currentPath}[${index}]`);
          }
        });
      } else {
        checkValue(value, currentPath);
      }
    }
  };
  
  traverse(sanitizedData);
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// Security headers for requests
export const getSecurityHeaders = (): Record<string, string> => {
  const csrfToken = getCSRFToken();
  
  return {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    ...(csrfToken && { 'X-CSRF-Token': csrfToken })
  };
};

// Enhanced error handling with security considerations
export const handleSecurityError = (error: any, context?: string, userId?: string) => {
  const sanitizedError = Sanitizer.sanitizeLog(String(error));
  console.error(`Security error${context ? ` in ${context}` : ''}:`, sanitizedError);
  
  // Don't expose sensitive error details to user
  const userMessage = 'Güvenlik nedeniyle işlem gerçekleştirilemedi. Lütfen tekrar deneyin.';
  toast.error(userMessage);
  
  // Log security incident
  const incident = {
    timestamp: new Date().toISOString(),
    error: sanitizedError,
    context: context || 'unknown',
    userId: userId || 'anonymous',
    userAgent: navigator.userAgent,
    url: window.location.href
  };
  
  // In production, send to monitoring service
  if (import.meta.env.PROD) {
    // Send to monitoring service
    console.log('Security incident logged:', incident);
    // TODO: Implement actual monitoring service integration
  }
};

// Content Security Policy helper
export const setCSPHeaders = (): void => {
  const meta = document.createElement('meta');
  meta.httpEquiv = 'Content-Security-Policy';
  meta.content = [
    "default-src 'self'",
    "script-src 'self' 'unsafe-inline' 'unsafe-eval'",
    "style-src 'self' 'unsafe-inline'",
    "img-src 'self' data: https:",
    "font-src 'self' data:",
    "connect-src 'self' https://api.gemini.google.com https://*.supabase.co wss://*.supabase.co https://www.googleapis.com https://*.googleapis.com https://nominatim.openstreetmap.org https://*.openstreetmap.org https://climate-api.open-meteo.com https://archive-api.open-meteo.com https://api.open-meteo.com https://power.larc.nasa.gov",
    "frame-src 'self' https://drive.google.com https://docs.google.com",
    "object-src 'none'",
    "base-uri 'self'"
  ].join('; ');
  
  document.head.appendChild(meta);
};

// Initialize security measures
export const initializeSecurity = (): void => {
  // Set CSRF token
  setCSRFToken();
  
  // Set CSP headers
  setCSPHeaders();
  
  // Disable right-click in production
  if (import.meta.env.PROD) {
    document.addEventListener('contextmenu', (e) => e.preventDefault());
  }
};

